import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { verifyNetwork } from '@/utils/networkVerification';

interface CheckInLocation {
  latitude: number;
  longitude: number;
  accuracy: number;
}

export function useCheckIn() {
  const { user } = useAuth();

  const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
    const R = 6371e3; // Earth's radius in meters
    const φ1 = lat1 * Math.PI / 180;
    const φ2 = lat2 * Math.PI / 180;
    const Δφ = (lat2 - lat1) * Math.PI / 180;
    const Δλ = (lon2 - lon1) * Math.PI / 180;

    const a = Math.sin(Δφ/2) * Math.sin(Δφ/2) +
              Math.cos(φ1) * Math.cos(φ2) *
              Math.sin(Δλ/2) * Math.sin(Δλ/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));

    return R * c;
  };

  const checkInToEvent = async (
    eventId: string,
    eventData?: { 
      location_latitude?: number; 
      location_longitude?: number; 
      location_radius?: number; 
      requires_gps?: boolean;
      start_time?: string;
      end_time?: string;
      enforce_time_window?: boolean;
      check_in_open_offset_minutes?: number;
      check_in_close_offset_minutes?: number;
      event_format?: 'in_person' | 'online' | 'hybrid';
      network_verification_enabled?: boolean;
      network_verification_mode?: string;
      allowed_ip_ranges?: string[];
      allowed_wifi_ssids?: string[];
      event_date?: string; // To track which occurrence of a recurring event
      is_recurring?: boolean;
    },
    location?: CheckInLocation,
    method?: 'gps' | 'qr'
  ) => {
    if (!user) {
      toast.error('Please log in to check in');
      return false;
    }

    // Network verification check
    if (eventData?.network_verification_enabled && eventData?.network_verification_mode) {
      const mode = eventData.network_verification_mode as 'ip_only' | 'wifi_only' | 'ip_or_wifi' | 'ip_and_wifi';
      const networkValid = await verifyNetwork(
        mode,
        eventData.allowed_ip_ranges,
        eventData.allowed_wifi_ssids
      );
      
      if (!networkValid) {
        return false;
      }
    }

    // For online events, we don't need GPS or QR - simple check-in
    if (eventData?.event_format === 'online') {
      // Online events can be checked in without location verification
      // but we still want to track attendance
    } else {
      // For in-person and hybrid events, enforce check-in method
      if (!method) {
        toast.error('You must use GPS or scan a QR code to check in.');
        return false;
      }
    }

    // Skip location validation for online events
    if (eventData?.event_format !== 'online') {
      if (method === 'gps') {
        // Check if event has GPS coordinates configured
        if (!eventData?.location_latitude || !eventData?.location_longitude) {
          toast.error('This event doesn\'t have a GPS location configured—please scan the QR to check in.');
          return false;
        }

        // Require location for GPS method
        if (!location) {
          toast.error('GPS verification is required for this event. Please enable location access.');
          return false;
        }

        // Calculate distance and enforce radius for GPS method
        const distance = calculateDistance(
          location.latitude,
          location.longitude,
          eventData.location_latitude,
          eventData.location_longitude
        );

        const maxDistance = eventData.location_radius || 50; // Default to 50m as requested
        if (distance > maxDistance) {
          toast.error(`You must be within ${maxDistance}m of the event location to check in. You are ${Math.round(distance)}m away.`);
          return false;
        }
      }
    }

    // For QR method with GPS-enabled events, still verify location (only for in-person/hybrid)
    if (eventData?.event_format !== 'online' && method === 'qr' && eventData?.requires_gps) {
      // Check if event has GPS coordinates configured
      if (!eventData?.location_latitude || !eventData?.location_longitude) {
        toast.error('This event has GPS verification enabled but no location is configured.');
        return false;
      }

      // Require location for QR method when GPS is enabled
      if (!location) {
        toast.error('This event requires GPS verification. Please enable location access to check in.');
        return false;
      }

      // Calculate distance and enforce radius for QR method when GPS is enabled
      const distance = calculateDistance(
        location.latitude,
        location.longitude,
        eventData.location_latitude,
        eventData.location_longitude
      );

      const maxDistance = eventData.location_radius || 50; // Default to 50m as requested
      if (distance > maxDistance) {
        toast.error(`You must be within ${maxDistance}m of the event location to check in, even with QR code. You are ${Math.round(distance)}m away.`);
        return false;
      }
    }

    // Track if check-in is late (declare before time window check)
    let isLateCheckIn = false;

    // Frontend time window pre-check for immediate feedback
    if (eventData?.start_time && eventData?.end_time) {
      const now = new Date();
      const startTime = new Date(eventData.start_time);
      const endTime = new Date(eventData.end_time);
      
      // If enforce_time_window is enabled, use the offset logic
      if (eventData.enforce_time_window) {
        const openOffset = eventData.check_in_open_offset_minutes || 0;
        const closeOffset = eventData.check_in_close_offset_minutes || 0;
        
        const openAt = new Date(startTime.getTime() - (openOffset * 60 * 1000));
        const closeAt = new Date(endTime.getTime() + (closeOffset * 60 * 1000));
        
        if (now < openAt) {
          toast.error(`Check-in opens at ${openAt.toLocaleString()}. Please wait until then.`);
          return false;
        }
        
        if (now > closeAt) {
          toast.error(`Check-in closed at ${closeAt.toLocaleString()}. You can no longer check in to this event.`);
          return false;
        }
      } else {
        // Default behavior: can't check in before event starts or after it ends
        if (now < startTime) {
          toast.error(`This event hasn't started yet. Check-in opens at ${startTime.toLocaleString()}.`);
          return false;
        }
        
        if (now > endTime) {
          toast.error(`This event has already ended at ${endTime.toLocaleString()}.`);
          return false;
        }
      }
      
      // Determine if check-in is late based on grace period
      // Check if late threshold is defined (could be 0 which is valid)
      if (eventData.enforce_time_window && (eventData as any).late_threshold_minutes !== undefined) {
        const lateThresholdMinutes = (eventData as any).late_threshold_minutes;
        const lateThreshold = new Date(startTime.getTime() + (lateThresholdMinutes * 60 * 1000));
        isLateCheckIn = now > lateThreshold;
        
        console.log('⏰ Late check-in determination:', {
          now: now.toISOString(),
          startTime: startTime.toISOString(),
          lateThreshold: lateThreshold.toISOString(),
          graceMinutes: lateThresholdMinutes,
          isLate: isLateCheckIn
        });
      }
    }

    try {
      console.log('🎯 Attempting check-in for:', { eventId, userId: user.id, method });
      
      // Check if user has already checked in to this event
      const { data: existingAttendance } = await supabase
        .from('attendance')
        .select('id')
        .eq('event_id', eventId)
        .eq('user_id', user.id)
        .maybeSingle();

      if (existingAttendance) {
        toast.error('You have already checked in to this event. Each event can only be attended once.');
        return false;
      }
      
      // Fetch full event data to check restrictions
      const { data: fullEventData } = await supabase
        .from('events')
        .select('event_type, audience_mode, scope, agency_id, district_id')
        .eq('id', eventId)
        .single();

      if (!fullEventData) {
        toast.error('Event not found');
        return false;
      }

      const { event_type: eventType, audience_mode: audienceMode, scope: eventScope, agency_id: eventAgencyId, district_id: eventDistrictId } = fullEventData;
      
      // Check if user is registered for RSVP events (applies to ALL users including admins)
      if (eventType === 'optional_rsvp') {
        const { data: registrationData } = await supabase
          .from('event_registrations')
          .select('*')
          .eq('event_id', eventId)
          .eq('user_id', user.id)
          .eq('status', 'registered')
          .single();

        if (!registrationData) {
          toast.error('You must register for this event before checking in');
          return false;
        }
      }

      // Check role for bypassing organizational restrictions
      const { data: profileData } = await supabase
        .from('profiles')
        .select('role')
        .eq('user_id', user.id)
        .single();

      const isAdmin = profileData?.role === 'admin';
      
      if (!isAdmin) {

        // Check group restrictions for organizational audience
        if (audienceMode === 'organizational') {
          const { data: eventGroups } = await supabase
            .from('event_group_assignments')
            .select('group_id')
            .eq('event_id', eventId);

          if (eventGroups && eventGroups.length > 0) {
            // Check if user is member of any assigned group
            const { data: userGroups } = await supabase
              .from('group_members')
              .select('group_id')
              .eq('user_id', user.id)
              .in('group_id', eventGroups.map(g => g.group_id));

            if (!userGroups || userGroups.length === 0) {
              toast.error('You are not in the required group to check in to this event');
              return false;
            }
          }
        }

        // Check agency restrictions
        if (eventScope === 'agency' && eventAgencyId) {
          const { data: userAgency } = await supabase
            .from('agency_members')
            .select('agency_id')
            .eq('user_id', user.id)
            .eq('agency_id', eventAgencyId)
            .maybeSingle();

          if (!userAgency) {
            toast.error('You are not in the required agency to check in to this event');
            return false;
          }
        }

        // Check district restrictions
        if (eventScope === 'district' && eventDistrictId) {
          // Get user's agencies
          const { data: userAgencies } = await supabase
            .from('agency_members')
            .select('agency_id')
            .eq('user_id', user.id);

          console.log('🏢 District check:', { 
            eventDistrictId, 
            userAgencies,
            userAgencyIds: userAgencies?.map(a => a.agency_id)
          });

          if (userAgencies && userAgencies.length > 0) {
            // Get districts of user's agencies
            const { data: agencies } = await supabase
              .from('agencies')
              .select('district_id')
              .in('id', userAgencies.map(a => a.agency_id));

            console.log('🏢 User agencies data:', agencies);
            
            const userDistrictIds = agencies?.map(a => a.district_id).filter(Boolean) || [];
            
            console.log('🏢 District comparison:', {
              eventDistrictId,
              eventDistrictIdType: typeof eventDistrictId,
              userDistrictIds,
              userDistrictIdsTypes: userDistrictIds.map(id => typeof id),
              includes: userDistrictIds.includes(eventDistrictId)
            });
            
            if (!userDistrictIds.includes(eventDistrictId)) {
              toast.error('You are not in the required district to check in to this event');
              return false;
            }
          } else {
            toast.error('You are not in the required district to check in to this event');
            return false;
          }
        }
      }
      
      const attendanceData: any = {
        event_id: eventId,
        user_id: user.id,
        location_verified: false,
        status: isLateCheckIn ? 'late' : 'attended',
        points_earned: isLateCheckIn ? 3 : 5,
        // For recurring events, store the occurrence date to track specific instances
        occurrence_date: null // No longer needed - events are individual records
      };

      if (location && eventData?.location_latitude && eventData?.location_longitude && eventData?.event_format !== 'online') {
        attendanceData.check_in_latitude = location.latitude;
        attendanceData.check_in_longitude = location.longitude;
        
        const distance = calculateDistance(
          location.latitude,
          location.longitude,
          eventData.location_latitude,
          eventData.location_longitude
        );
        
        attendanceData.distance_from_event = distance;
        attendanceData.location_verified = distance <= (eventData.location_radius || 50);
      } else if (eventData?.event_format === 'online') {
        // For online events, mark as verified since no location is needed
        attendanceData.location_verified = true;
        attendanceData.distance_from_event = null;
      }

      const { error } = await supabase
        .from('attendance')
        .insert([attendanceData]);

      if (error) throw error;

      const statusMessage = isLateCheckIn 
        ? 'Checked in late - 3 points earned' 
        : 'Successfully checked in - 5 points earned!';
      
      toast.success(statusMessage);
      return true;
    } catch (error: any) {
      console.error('❌ Check-in error:', error);
      console.log('Error details:', { code: error.code, message: error.message, eventId, userId: user.id });
      
      if (error.code === '23505') {
        toast.error(`You have already checked in to this event. Each event can only be attended once.`);
      } else if (error.message) {
        // Handle time window validation errors with user-friendly messages
        const errorMessage = error.message;
        
        if (errorMessage.includes('Check-in not yet open')) {
          const match = errorMessage.match(/Opens at (.+)/);
          const openTime = match ? new Date(match[1]).toLocaleString() : 'the scheduled time';
          toast.error(`Check-in is not yet open. You can check in starting at ${openTime}.`);
        } else if (errorMessage.includes('Check-in has closed')) {
          const match = errorMessage.match(/Closed at (.+)/);
          const closeTime = match ? new Date(match[1]).toLocaleString() : 'the deadline';
          toast.error(`Check-in has closed. The deadline was ${closeTime}.`);
        } else if (errorMessage.includes('Event is not active')) {
          toast.error('This event is not currently active.');
        } else if (errorMessage.includes('Event has no time window configured')) {
          toast.error('This event has invalid time configuration. Please contact an administrator.');
        } else {
          toast.error(errorMessage);
        }
      } else {
        toast.error('Failed to check in');
      }
      return false;
    }
  };

  return {
    checkInToEvent,
    calculateDistance
  };
}